<template>
    <v-container fluid class="pa-0 business-view">
        <underline-tabs top-border :items="tabs" :value="tab" @change="onChangeTab" />

        <div v-if="usesPeriod && !lockedFeature" class="business-bar"
            :class="{ 'business-bar--divided': tab !== 'statistics' }">
            <period-bar :value="period" @change="onChangePeriod">
                <template v-slot:actions>
                    <export-button :filename="exportName" :build="buildExport" />
                </template>
            </period-bar>
        </div>

        <underline-tabs v-if="tab === 'statistics'" dense top-border :items="statsTabs" :value="statsTab"
            @change="statsTab = $event" />

        <div class="business-body">
            <plan-required-content v-if="lockedFeature" inline :feature="lockedFeature" />

            <template v-else-if="tab === 'statistics'">
                <stats-basic v-if="statsTab === 'basic'" :company-id="companyId" :period="period" :cash="cash"
                    :payments="yearPayments" :clients-income="clientsIncome"
                    :clients-income-by-period="clientsIncomeByPeriod" :loading="loadingCash" />

                <stats-audience v-else-if="statsTab === 'audience'" :company-id="companyId" :period="period" />

                <stats-financial v-else-if="statsTab === 'financial'" :company-id="companyId" :period="period" :cash="cash"
                    :clients-income="clientsIncome" :clients-income-by-period="clientsIncomeByPeriod"
                    :loading="loadingCash" />

                <stats-marketing v-else :company-id="companyId" :period="period" :cash="cash" :loading="loadingCash"
                    @change:funnel="onChangeFunnel" />
            </template>

            <clients-tab v-else-if="tab === 'clients'" :company-id="companyId" :period="period"
                :payments="yearPayments" @change:payments="loadClientsIncome(true)" />

            <cash-tab v-else-if="tab === 'cash'" :value="cash" :loading="loadingCash" :copying="copying"
                :can-copy-previous="canCopyPrevious" :saved="cashExists" :clients-income="clientsIncome"
                @change="onChangeCash" @copy:previous="copyPreviousCash" />

            <shifts-tab v-else-if="tab === 'schedule'" :company-id="companyId" />

            <div v-else class="business-panel">
                <span class="business-pending">{{ $t('business.coming_soon') }}</span>
            </div>
        </div>
    </v-container>
</template>

<script>
import UnderlineTabs from "@/components/common/UnderlineTabs";
import PeriodBar from "@/components/common/PeriodBar";
import ClientsTab from "@/components/company/business/ClientsTab";
import CashTab from "@/components/company/business/CashTab";
import StatsBasic from "@/components/company/business/StatsBasic";
import StatsAudience from "@/components/company/business/StatsAudience";
import StatsFinancial from "@/components/company/business/StatsFinancial";
import StatsMarketing from "@/components/company/business/StatsMarketing";
import ShiftsTab from "@/components/company/business/ShiftsTab";
import ExportButton from "@/components/company/business/ExportButton";
import PlanRequiredContent from "@/components/common/PlanRequiredContent";
import {
    cloneCash, defaultCash, loadCash, loadCashYear, loadRoster, loadSettings, loadYearPayments, mergeRoster,
    previousPeriod, saveCash, setCurrency,
} from "@/utils/Business";
import { buildCashSheet, buildStatsSheets } from "@/utils/BusinessExport";

const PERIOD_TABS = ['statistics', 'cash']

export default {
    name: "CompanyBusiness",
    components: {
        UnderlineTabs,
        PeriodBar,
        ClientsTab,
        CashTab,
        StatsBasic,
        StatsAudience,
        StatsFinancial,
        StatsMarketing,
        ShiftsTab,
        ExportButton,
        PlanRequiredContent,
    },
    data() {
        return {
            tab: 'clients',
            statsTab: 'basic',
            period: '',
            cash: null,
            cashExists: false,
            canCopyPrevious: false,
            loadingCash: true,
            copying: false,
            yearPayments: [],
            incomeYear: null,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.currentUser?.uid || null
        },
        tabs(vm = this) {
            return [
                { name: vm.$t('business.clients'), value: 'clients', feature: 'businessClients' },
                { name: vm.$t('business.statistics'), value: 'statistics', feature: 'businessBasicStatistics' },
                { name: vm.$t('business.cash'), value: 'cash', feature: 'businessCash' },
                { name: vm.$t('business.schedule'), value: 'schedule' },
            ]
        },
        statsTabs(vm = this) {
            return [
                { name: vm.$t('business.stats_basic'), value: 'basic', feature: 'businessBasicStatistics' },
                { name: vm.$t('business.stats_financial'), value: 'financial', feature: 'businessAdvancedStatistics' },
                { name: vm.$t('business.stats_audience'), value: 'audience', feature: 'businessAdvancedStatistics' },
                { name: vm.$t('business.stats_marketing'), value: 'marketing', feature: 'businessAdvancedStatistics' },
            ]
        },
        lockedFeature(vm = this) {
            const current = vm.tabs.find(item => item.value === vm.tab)
            if (current?.feature && !vm.can(current.feature)) return current.feature
            if (vm.tab !== 'statistics') return null
            const stats = vm.statsTabs.find(item => item.value === vm.statsTab)
            return stats?.feature && !vm.can(stats.feature) ? stats.feature : null
        },
        usesPeriod(vm = this) {
            return PERIOD_TABS.includes(vm.tab)
        },
        clientsIncomeByPeriod(vm = this) {
            const byPeriod = {}
            vm.yearPayments.forEach(payment => {
                byPeriod[payment.period] = (byPeriod[payment.period] || 0) + (Number(payment.amount) || 0)
            })
            return byPeriod
        },
        clientsIncome(vm = this) {
            return vm.clientsIncomeByPeriod[vm.period] || 0
        },
        exportName(vm = this) {
            const name = vm.tab === 'cash' ? vm.$t('business.cash') : vm.$t('business.statistics')
            return `${name} ${vm.period}`
        },
    },
    created() {
        this.restoreFromQuery()
    },
    mounted() {
        const vm = this
        const allowed = ['businessClients', 'businessBasicStatistics', 'businessCash']
        if (!allowed.some(feature => vm.can(feature))) return
        vm.loadCurrency()
        vm.loadCashPeriod()
    },
    // La moneda es del negocio abierto: si no se limpia, la siguiente cuenta
    // formatea con la moneda de la anterior hasta que carguen sus ajustes.
    destroyed() {
        setCurrency(null)
    },
    methods: {
        async loadCurrency() {
            const vm = this
            if (!vm.companyId) return
            try {
                await loadSettings(vm.companyId)
            } catch (error) {
                console.error('[business] loadSettings', error?.code, error?.message)
            }
        },
        restoreFromQuery() {
            const vm = this
            const { tab, period } = vm.$route.query || {}
            if (vm.tabs.some(item => item.value === tab)) vm.tab = tab
            vm.period = /^\d{4}-\d{2}$/.test(period || '') ? period : vm.$moment().format('YYYY-MM')
        },
        syncQuery() {
            const vm = this
            const query = { ...vm.$route.query, tab: vm.tab, period: vm.period }
            if (query.tab === vm.$route.query.tab && query.period === vm.$route.query.period) return
            vm.$router.replace({ query }).catch(() => { })
        },
        // Caja exporta solo el mes; Estadisticas necesita padron y ano de caja, que
        // salen de los mismos loaders cacheados que ya usan las pestanas.
        async buildExport() {
            const vm = this
            if (vm.tab === 'cash') {
                return buildCashSheet({ cash: vm.cash, clientsIncome: vm.clientsIncome, period: vm.period })
            }
            const [roster, months] = await Promise.all([
                loadRoster(vm.companyId),
                loadCashYear(vm.companyId, Number(String(vm.period).slice(0, 4))),
            ])
            return buildStatsSheets({
                roster: mergeRoster(roster.clients, roster.students),
                planTypes: roster.planTypes,
                payments: vm.yearPayments,
                cash: vm.cash,
                months,
                clientsIncomeByPeriod: vm.clientsIncomeByPeriod,
                period: vm.period,
                sportLabel: vm.sportLabel,
            })
        },
        onChangeTab(tab) {
            const vm = this
            vm.tab = tab
            vm.syncQuery()
        },
        onChangePeriod(period) {
            const vm = this
            vm.period = period
            vm.syncQuery()
            vm.loadCashPeriod()
        },
        // Se trae el año entero, no solo el mes: la evolución anual de Financiero
        // necesita los pagos de CADA mes, y muchos meses no tienen caja cargada.
        async loadClientsIncome(force = false) {
            const vm = this
            if (!vm.companyId || !vm.period) return
            const year = String(vm.period).slice(0, 4)
            if (!force && vm.incomeYear === year) return
            try {
                vm.yearPayments = await loadYearPayments(vm.companyId, year)
                vm.incomeYear = year
            } catch (error) {
                console.error('[business] loadYearPayments', error?.code, error?.message)
                vm.yearPayments = []
                vm.incomeYear = null
            }
        },
        async checkPreviousCash() {
            const vm = this
            if (vm.cashExists) return (vm.canCopyPrevious = false)
            try {
                vm.canCopyPrevious = !!(await loadCash(vm.companyId, previousPeriod(vm.period)))
            } catch (error) {
                vm.canCopyPrevious = false
            }
        },
        async loadCashPeriod() {
            const vm = this
            if (!vm.companyId || !vm.period) return
            vm.loadingCash = true
            vm.loadClientsIncome()
            try {
                const data = await loadCash(vm.companyId, vm.period)
                vm.cashExists = !!data
                vm.cash = data || defaultCash()
                // No bloquea la carga: es solo para habilitar "copiar del mes anterior".
                vm.checkPreviousCash()
            } catch (error) {
                console.error('[business] loadCash', error?.code, error?.message)
                vm.cash = defaultCash()
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loadingCash = false
            }
        },
        // Si el guardado falla hay que volver atras: dejar el valor en pantalla hacia
        // creer que se habia guardado (asi se perdio la caja de julio y agosto).
        async onChangeCash(cash) {
            const vm = this
            const previous = vm.cash
            vm.cash = cash
            try {
                await saveCash(vm.companyId, vm.period, cash)
                vm.cashExists = true
                vm.canCopyPrevious = false
            } catch (error) {
                console.error('[business] saveCash', error?.code, error?.message)
                vm.cash = previous
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        onChangeFunnel(funnel) {
            this.onChangeCash({ ...this.cash, funnel })
        },
        async copyPreviousCash() {
            const vm = this
            vm.copying = true
            try {
                const previous = await loadCash(vm.companyId, previousPeriod(vm.period))
                if (!previous) return vm.toast(vm.$t('business.no_previous_month'), 'error')
                await vm.onChangeCash(cloneCash(previous))
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.copying = false
            }
        },
    },
}
</script>

<style scoped>
.business-bar {
    padding: 0 24px;
}

.business-bar--divided {
    border-bottom: 1px solid var(--v-outline-base);
}

.business-body {
    padding: 16px 24px;
}

.business-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    border: 1px dashed var(--v-outline-base);
    border-radius: 12px;
}

.business-pending {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 600px) {
    .business-bar {
        padding: 0 12px;
    }

    .business-body {
        padding: 12px;
    }
}
</style>
